import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Label } from '../../../../shared/compass-ui'
import ControlledTextInput from '../../../shared/ControlledTextInput'
import ControlledNumericInput from '../../../shared/ControlledNumericInput'
import HiddenFieldMirror from '../../../shared/HiddenFieldMirror'
import SuggestionText from '../../../shared/SuggestionText'
import TextInputError from '../../../shared/TextInputError'
import stateOptions from '../../../../constants/states'
import Autocomplete, { ADDRESS_ERROR_FIELDS, type NestedSuggestions } from './Autocomplete'
import { useAnalytics } from '../../../../shared/context/Analytics'
import useFormStore from '../../../../shared/hooks/useFormStore'
import { ZIP_CODE_PLACEHOLDER } from '../../../../constants/formFields'

const Address = () => {
  const [formStore] = useFormStore()
  const { isMulticardFlow } = formStore
  const [showHelperText, setShowHelperText] = useState<boolean>(false)

  const {
    clearErrors,
    register,
    setFocus,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext()
  const { unit, city, state, zip } = errors
  const { suggestions } = errors as NestedSuggestions
  const { track, events } = useAnalytics()
  const isStateError =
    typeof state?.message === 'string' || typeof errors?.generalAddress?.message === 'string'

  useEffect(() => {
    if (track) {
      if (suggestions) {
        // Send Segment events when suggestions (city, state, or zip code) are shown
        if (typeof suggestions.city?.message === 'string') {
          track(events.ADDRESS_VALIDATION.AddressSuggestionCityView)
        }
        if (typeof suggestions.state?.message === 'string') {
          track(events.ADDRESS_VALIDATION.AddressSuggestionStateView)
        }
        if (typeof suggestions.zip?.message === 'string') {
          track(events.ADDRESS_VALIDATION.AddressSuggestionZipCodeView)
        }
      }
      if (typeof state?.message === 'string') {
        // Send Segment event when user selects an invalid state
        if (state?.type !== 'invalid_enum_value') {
          track(events.VIEW_FORM.InvalidStateMessage, { message: state.message })
        }
      }
    }
  }, [track, events, suggestions, state])

  // Used to clear generic errors on any field change
  const handleChange = () => {
    clearErrors(ADDRESS_ERROR_FIELDS)
  }

  const handleFocus = () => {
    if (isMulticardFlow) {
      setShowHelperText(true)
    }
  }

  const handleBlur = () => {
    if (isMulticardFlow) {
      setShowHelperText(false)
    }
  }

  return (
    <div
      className="grid grid-cols-4 md:grid-cols-6 gap-6"
      data-testid="address-form"
      onChange={handleChange}
    >
      <div className="col-span-full md:col-span-4">
        <Autocomplete showHelperText={showHelperText} onBlur={handleBlur} onFocus={handleFocus} />
      </div>
      <div className="col-span-full md:col-span-2">
        <Label htmlFor="unit-input">Unit / Apartment #</Label>
        <ControlledTextInput
          data-testid="unit-input"
          id="unit-input"
          name="unit"
          maxLength={32}
          error={typeof unit?.message === 'string'}
          onBlur={handleBlur}
          onFocus={handleFocus}
          autoComplete="address-line2"
        />
        {typeof unit?.message === 'string' && <TextInputError>{unit.message}</TextInputError>}
      </div>
      <div className="col-span-full md:col-span-2">
        <Label htmlFor="city-input">City</Label>
        <ControlledTextInput
          data-testid="city-input"
          id="city-input"
          name="city"
          error={
            typeof errors?.generalAddress?.message === 'string' || typeof city?.message === 'string'
          }
          onBlur={handleBlur}
          onFocus={handleFocus}
          autoComplete="address-level2"
        />
        {typeof city?.message === 'string' && <TextInputError>{city.message}</TextInputError>}
        {typeof suggestions?.city?.message === 'string' && (
          <SuggestionText data-testid="city-suggestion">
            Did you mean{' '}
            <button
              className="underline text-blue-600"
              onClick={() => {
                track?.(events.ADDRESS_VALIDATION.AddressSuggestionCityAccept)
                setValue('city', suggestions?.city?.message)
                clearErrors('suggestions.city')
                setFocus('city')
              }}
              type="button"
            >
              {suggestions?.city?.message}
            </button>
            ?
          </SuggestionText>
        )}
      </div>
      <div className="col-span-full md:col-span-2">
        <Label>
          State
          <select
            {...register('state', {
              value: 'default',
              onChange: () => trigger('state'),
            })}
            className={classNames(
              'bg-white',
              'h-[60px]',
              'border-2',
              'font-normal',
              'p-4',
              'pr-xl',
              'rounded',
              'bg-chevron',
              'bg-no-repeat',
              'bg-[right_16px_center]',
              {
                'border-red-600 outline-red-600': isStateError,
                'border-ink-400 focus:border-blue-600 outline-blue-600': !isStateError,
              }
            )}
            data-testid="state-input"
            id="state-input"
            aria-describedby={typeof state?.message === 'string' ? 'state-error' : undefined}
            onBlur={handleBlur}
            onFocus={handleFocus}
          >
            <option disabled value="default"></option>
            {stateOptions.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Label>
        <HiddenFieldMirror name="state" autoComplete="address-level1" />
        {typeof state?.message === 'string' && (
          <TextInputError id="state-error">
            {state?.type === 'invalid_enum_value' ? 'This field is required' : state.message}
          </TextInputError>
        )}
        {typeof suggestions?.state?.message === 'string' && (
          <SuggestionText data-testid="state-suggestion">
            Did you mean{' '}
            <button
              className="underline text-blue-600"
              onClick={() => {
                track?.(events.ADDRESS_VALIDATION.AddressSuggestionStateAccept)
                setValue('state', suggestions?.state?.message)
                clearErrors('suggestions.state')
                setFocus('state')
              }}
              type="button"
            >
              {suggestions?.state?.message}
            </button>
            ?
          </SuggestionText>
        )}
      </div>
      <div className="col-span-full md:col-span-2">
        <Label htmlFor="zip-code-input">Zip Code</Label>
        <ControlledNumericInput
          name="zip"
          data-testid="zip-code-input"
          id="zip-code-input"
          error={
            typeof zip?.message === 'string' || typeof errors?.generalAddress?.message === 'string'
          }
          placeholder={ZIP_CODE_PLACEHOLDER}
          onBlur={handleBlur}
          onFocus={handleFocus}
          autoComplete="postal-code"
        />
        {typeof zip?.message === 'string' && <TextInputError>{zip.message}</TextInputError>}
        {typeof suggestions?.zip?.message === 'string' && (
          <SuggestionText data-testid="zip-suggestion">
            Did you mean{' '}
            <button
              className="underline text-blue-600"
              onClick={() => {
                track?.(events.ADDRESS_VALIDATION.AddressSuggestionZipCodeAccept)
                setValue('zip', suggestions?.zip?.message?.slice(0, 5))
                clearErrors('suggestions.zip')
                setFocus('zip')
              }}
              type="button"
            >
              {suggestions?.zip?.message.slice(0, 5)}
            </button>
            ?
          </SuggestionText>
        )}
      </div>
      {typeof errors?.generalAddress?.message === 'string' && (
        <p className="font-semibold mt-2 text-red-600 col-span-full">
          {errors.generalAddress.message}
        </p>
      )}
    </div>
  )
}

export default Address
